import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MarkdownIt from 'markdown-it';
import toast from 'react-hot-toast';
import { RootState, useAppDispatch, useAppSelector } from '../store';
import { UserSettings } from '../Types';
import { setSettings } from '../Store/features/settings/settingsSlice';
import { setDemoInstance, setShowUserStatistics } from '../Store/features/environment/environmentSlice';
import { StoreSettings } from '../Types/StoreTypes';
import { EmbeddingModel, Model, VoiceModel } from '../Types';
import { SettingsTabName } from '../Types/Enums';
import { settingsService } from '../Services/settingsService';
import { embeddingModelService } from '../Services/embeddingModelService';
import { voiceModelService } from '../Services/voiceModelService';
import useModal from '../components/Modal/useModal';
import { getBreakpoint } from '../utils/breakpoint';

const useSettings = () => {
    const navigate = useNavigate();
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const windowWidth = useAppSelector((state: RootState) => state.environment.windowWidth);
    const settings = useAppSelector((state: RootState) => state.settings);
    const dispatch = useAppDispatch();
    const demo = useAppSelector((state: RootState) => state.environment.demoInstance as boolean);

    const mdParser = new MarkdownIt();
    const [Currentsettings, setCurrentSettings] = useState<StoreSettings>(settings);
    const [currentTab, setCurrentTab] = useState<SettingsTabName>(SettingsTabName.general);
    const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);
    const [pendingAction, setPendingAction] = useState<string>('');
    const [models, setModels] = useState<Partial<Model>[]>([] as Partial<Model>[]);
    const [embeddingModels, setEmbeddingModels] = useState<Partial<EmbeddingModel>[]>([] as Partial<EmbeddingModel>[]);
    const [voiceModels, setVoiceModels] = useState<Partial<VoiceModel>[]>([] as Partial<VoiceModel>[]);
    const [importantChangesConfirmed, setImportantChangesConfirmed] = useState<{
        icapAddress: boolean;
        sensitivityLabels: boolean;
    }>({
        icapAddress: true,
        sensitivityLabels: true,
    });
    const [shouldSetDsgvoTimestamp, setShouldSetDsgvoTimestamp] = useState(false);
    const modal = useModal();
    const notifySuccess = useCallback(() => {
        toast.success(i18n.translate({ id: 'toast.settingsSaved', sourceName: 'Settings' }));
    }, [i18n]);

    useEffect(() => {
        if (!userSettings.isLoggedIn && settingsLoaded) return;
        if (!settings || demo) {
            settingsService.getSettings(demo).then((response: any) => {
                dispatch(setSettings(response.data));
                dispatch(setShowUserStatistics(response.data.userStatistic));
                setSettingsLoaded(true);
            });
        } else {
            setSettingsLoaded(true);
        }
    }, [userSettings.isLoggedIn, settings, dispatch, settingsLoaded, navigate, demo]);

    useEffect(() => {
        if (userSettings.isLoggedIn && userSettings.isSuperAdmin) {
            settingsService.getModels().then((response) => {
                setModels(Array.isArray(response?.data) ? response.data : []);
            });

            embeddingModelService.getAllModelsPaginated().then((response) => {
                setEmbeddingModels(Array.isArray(response) ? response : []);
            });

            voiceModelService.getModels(demo).then((response: { data?: { data?: VoiceModel[] } }) => {
                setVoiceModels(Array.isArray(response?.data?.data) ? response.data.data : []);
            });
        }
    }, [userSettings.isLoggedIn, userSettings.isSuperAdmin, demo]);

    const updateCurrentSettings = useCallback((updates: Partial<StoreSettings>) => {
        setCurrentSettings((prevSettings) => ({
            ...prevSettings,
            ...updates,
        }));
    }, []);

    const handleSensitivityLabelChange = useCallback((index: number, value: string) => {
        setCurrentSettings((prev) => ({
            ...prev,
            sensitivityLabels: prev.sensitivityLabels.map((label, i) => (i === index ? value : label)),
        }));
    }, []);

    const removeSensitivityLabel = useCallback((index: number) => {
        setCurrentSettings((prev) => ({
            ...prev,
            sensitivityLabels: prev.sensitivityLabels.filter((_, i) => i !== index),
        }));
    }, []);

    const removeDlpRegex = useCallback((index: number) => {
        setCurrentSettings((prev) => ({
            ...prev,
            dlpRegexes: prev.dlpRegexes.filter((_, i) => i !== index),
        }));
    }, []);

    const handleDlpRegexChange = useCallback((index: number, value?: string, label?: string) => {
        setCurrentSettings((prev) => ({
            ...prev,
            dlpRegexes: prev.dlpRegexes.map((regex, i) =>
                i === index ? { label: label ?? regex.label, regex: value ?? regex.regex } : regex,
            ),
        }));
    }, []);

    const handleAllowListChange = useCallback((index: number, value: string) => {
        setCurrentSettings((prev) => ({
            ...prev,
            allowList: prev.allowList.map((item, i) => (i === index ? value : item)),
        }));
    }, []);

    const removeAllowListItem = useCallback((index: number) => {
        setCurrentSettings((prev) => ({
            ...prev,
            allowList: prev.allowList.filter((_, i) => i !== index),
        }));
    }, []);

    const handleAdditionalProtocolChange = useCallback((index: number, value: string) => {
        setCurrentSettings((prev) => ({
            ...prev,
            additionalAllowedProtocols: (prev.additionalAllowedProtocols || []).map((item, i) =>
                i === index ? value : item,
            ),
        }));
    }, []);

    const removeAdditionalProtocol = useCallback((index: number) => {
        setCurrentSettings((prev) => ({
            ...prev,
            additionalAllowedProtocols: (prev.additionalAllowedProtocols || []).filter((_, i) => i !== index),
        }));
    }, []);

    const saveSettings = useCallback(
        async (licenseKeyOverride?: string, shouldSetDsgvoTimestamp?: boolean) => {
            const data = new FormData();
            data.append('maxCosts', Currentsettings.maxCosts.toString());
            data.append('dataProtectionText', Currentsettings.dataProtectionText);
            data.append('brandingTitle', (Currentsettings.brandingTitle ?? '').toString());
            data.append('demo', Currentsettings.demo.toString());
            data.append('betaActive', Currentsettings.betaActive.toString());
            data.append('licences', Currentsettings.licences.toString());
            data.append('apiKeyLicences', Currentsettings.apiKeyLicences.toString());
            data.append('userStatistics', Currentsettings.userStatistic.toString());
            data.append('prePaidActive', Currentsettings.prePaidActive.toString() || 'false');
            data.append('selfUpdateActive', Currentsettings.selfUpdateActive.toString() || 'false');
            data.append('supportLoginEnabled', Currentsettings.supportLoginEnabled.toString() || 'false');
            data.append('helpLinkVisible', Currentsettings.helpLinkVisible.toString());
            data.append('documentUpload', Currentsettings.features?.documentUpload?.toString() || 'false');
            data.append('prePaidNotifications', Currentsettings.prePaidNotifications.toString());
            data.append('proxyUrlHttp', Currentsettings.proxyUrlHttp);
            data.append('proxyUrlHttps', Currentsettings.proxyUrlHttps);
            data.append('proxyExceptions', Currentsettings.proxyExceptions ?? '');
            data.append('proxyCertificatePem', Currentsettings.proxyCertificatePem);
            data.append('icapAddress', Currentsettings.icapAddress);
            data.append('supportMailAddress', Currentsettings.supportMailAddress);
            data.append('summaryModel', Currentsettings.summaryModel ?? models[0].identifier);
            data.append('defaultModel', Currentsettings.defaultModel ?? models[0].identifier);
            const licenseKeyToSend =
                typeof licenseKeyOverride === 'string' && licenseKeyOverride.trim()
                    ? licenseKeyOverride.trim()
                    : typeof Currentsettings.licenseKey === 'string' && Currentsettings.licenseKey.trim()
                      ? Currentsettings.licenseKey.trim()
                      : null;
            if (licenseKeyToSend) {
                data.append('licenseKey', licenseKeyToSend);
            }
            data.append('accountId', Currentsettings.accountId);
            data.append('releaseChannel', Currentsettings.releaseChannel);
            data.append('instanceSystemPrompt', Currentsettings.instanceSystemPrompt);
            data.append('bestPracticeSystemPrompt', Currentsettings.bestPracticeSystemPrompt);
            if (Object.values(Currentsettings.loggerSettings).every((l) => !isNaN(l.retentionPeriod))) {
                data.append('loggerSettings', JSON.stringify(Currentsettings.loggerSettings));
            }
            data.append('openaiDocumentProcessingEnabled', Currentsettings.openaiDocumentProcessingEnabled.toString());
            data.append('knowledgebaseSearchMode', Currentsettings.knowledgebaseSearchMode);
            data.append('hybridSearchVectorWeight', (Currentsettings.hybridSearchVectorWeight ?? 0.5).toString());
            data.append('hybridSearchTextWeight', (Currentsettings.hybridSearchTextWeight ?? 0.5).toString());
            data.append('chunkSize', (Currentsettings.chunkSize || 2000).toString());
            data.append('chunkOverlap', (Currentsettings.chunkOverlap || 500).toString());
            data.append('deleteUserAfter', Currentsettings.deleteUserAfter.toString());
            if (Currentsettings.userSyncPullInterval && (Currentsettings.userSyncPullInterval ?? 0) >= 5) {
                data.append('userSyncPullInterval', Currentsettings.userSyncPullInterval.toString());
            }
            if (
                Currentsettings.chatDuration === 'infinite' ||
                !isNaN(parseInt(Currentsettings.chatDuration.split(':')[1]))
            ) {
                data.append('chatDuration', Currentsettings.chatDuration);
            }
            if (Currentsettings.embeddingModel || embeddingModels.length > 0) {
                data.append('embeddingModel', Currentsettings.embeddingModel ?? embeddingModels[0].identifier);
            }
            if (Currentsettings.ttsModel !== undefined) {
                data.append('ttsModel', Currentsettings.ttsModel || '');
            } else if (voiceModels.length > 0) {
                data.append('ttsModel', voiceModels[0].identifier ?? '');
            }
            if (Currentsettings.transcribeModel !== undefined) {
                data.append('transcribeModel', Currentsettings.transcribeModel || '');
            } else if (voiceModels.length > 0) {
                data.append('transcribeModel', voiceModels[0].identifier ?? '');
            }
            Currentsettings.sensitivityLabels.forEach((sensitivityLabel) => {
                data.append('sensitivityLabels[]', sensitivityLabel);
            });
            if (Currentsettings.dlpRegexes && Currentsettings.dlpRegexes.length > 0) {
                Currentsettings.dlpRegexes.forEach((dlpRegex, index) => {
                    data.append('dlpRegexes[' + index + '][label]', dlpRegex.label);
                    data.append('dlpRegexes[' + index + '][regex]', dlpRegex.regex);
                });
            } else {
                data.append('dlpRegexes[0]', '');
            }
            if (Currentsettings.dlpEntities && Currentsettings.dlpEntities.length > 0) {
                Currentsettings.dlpEntities.forEach((dlpEntity, index) => {
                    data.append('dlpEntities[' + index + ']', dlpEntity);
                });
            } else {
                data.append('dlpEntities[0]', '');
            }
            if (Currentsettings.activeDlpProvider) {
                data.append('activeDlpProvider', Currentsettings.activeDlpProvider);
            } else {
                data.append('activeDlpProvider', 'presidio');
            }
            if (Currentsettings.allowList && Currentsettings.allowList.length > 0) {
                Currentsettings.allowList.forEach((allowListItem) => {
                    data.append('allowList[]', allowListItem);
                });
            } else {
                data.append('allowList[0]', '');
            }
            if (Currentsettings.additionalAllowedProtocols && Currentsettings.additionalAllowedProtocols.length > 0) {
                Currentsettings.additionalAllowedProtocols.forEach((proto) => {
                    data.append('additionalAllowedProtocols[]', proto);
                });
            } else {
                data.append('additionalAllowedProtocols[0]', '');
            }
            if (shouldSetDsgvoTimestamp) {
                data.append('setNewDSGVOVersionTimestamp', 'true');
            }

            if (Currentsettings.theme.lightThemeImage) {
                if (typeof Currentsettings.theme.lightThemeImage === 'object') {
                    data.append(
                        'lightThemeImage',

                        new File([Currentsettings.theme.lightThemeImage], Currentsettings.theme.lightThemeImage.name, {
                            type: Currentsettings.theme.lightThemeImage.type,
                        }),
                    );
                }
            } else {
                data.append('deleteLightThemeImage', 'true');
            }

            if (Currentsettings.theme.darkThemeImage) {
                if (typeof Currentsettings.theme.darkThemeImage === 'object') {
                    data.append(
                        'darkThemeImage',

                        new File([Currentsettings.theme.darkThemeImage], Currentsettings.theme.darkThemeImage.name, {
                            type: Currentsettings.theme.darkThemeImage.type,
                        }),
                    );
                }
            } else {
                data.append('deleteDarkThemeImage', 'true');
            }

            if (Currentsettings.favicon) {
                if (typeof Currentsettings.favicon === 'object') {
                    data.append(
                        'favicon',
                        new File([Currentsettings.favicon], Currentsettings.favicon.name, {
                            type: Currentsettings.favicon.type,
                        }),
                    );
                }
            } else {
                data.append('deleteFavicon', 'true');
            }

            settingsService.saveSettings(data).then((response) => {
                dispatch(setSettings(response.data));
                dispatch(setShowUserStatistics(response.data.userStatistic));
                dispatch(setDemoInstance(response.data.demo));
                setSettingsLoaded(false);
                setCurrentSettings(response.data);
                setShouldSetDsgvoTimestamp(false);
                notifySuccess();
            });
        },
        [Currentsettings, dispatch, models, embeddingModels, notifySuccess, shouldSetDsgvoTimestamp],
    );

    const saveReleaseChannel = useCallback(
        (releaseChannel: string) => {
            const data = new FormData();
            data.append('releaseChannel', releaseChannel);
            settingsService.saveSettings(data).then((response) => {
                dispatch(setSettings(response.data));
            });
        },
        [dispatch],
    );

    const handleUpdateSettings = useCallback(
        (licenseKeyOverride?: string) => {
            if (demo) {
                notifySuccess();
                return;
            }

            if (!importantChangesConfirmed.icapAddress) {
                modal.confirm({
                    title: i18n.translate({ id: 'icapChangeConfirm.title', sourceName: 'Settings' }),
                    content: i18n.translate({ id: 'icapChangeConfirm.text', sourceName: 'Settings' }),
                    onConfirm: () => {
                        setImportantChangesConfirmed((prev) => ({
                            ...prev,
                            icapAddress: true,
                        }));
                        handleUpdateSettings();
                    },
                });
                return;
            }

            if (!importantChangesConfirmed.sensitivityLabels) {
                modal.confirm({
                    title: i18n.translate({ id: 'sensitivityLabelsChanged.title', sourceName: 'Settings' }),
                    content: i18n.translate({ id: 'sensitivityLabelsChanged.text', sourceName: 'Settings' }),
                    onConfirm: () => {
                        setImportantChangesConfirmed((prev) => ({
                            ...prev,
                            sensitivityLabels: true,
                        }));
                        handleUpdateSettings();
                    },
                });
                return;
            }

            const currentText = (Currentsettings.dataProtectionText ?? '').trim().replaceAll('\r', '');
            const savedText = (settings.dataProtectionText ?? '').trim().replaceAll('\r', '');
            const dataProtectionTextChanged = currentText !== savedText && currentText !== '';

            if (dataProtectionTextChanged) {
                modal.open({
                    title: i18n.translate({ id: 'dsgvoVersionTimestamp.title', sourceName: 'Settings' }),
                    content: i18n.translate({ id: 'dsgvoVersionTimestamp.text', sourceName: 'Settings' }),
                    primaryButton: {
                        text: i18n.translate({ id: 'dsgvoVersionTimestamp.confirm', sourceName: 'Settings' }),
                        onClick: () => {
                            saveSettings(licenseKeyOverride, true);
                        },
                    },
                    secondaryButton: {
                        text: i18n.translate({ id: 'dsgvoVersionTimestamp.cancel', sourceName: 'Settings' }),
                        onClick: () => {
                            saveSettings(licenseKeyOverride);
                        },
                    },
                    showCloseButton: false,
                    closeOnBackdropClick: false,
                });
                return;
            }

            if (Object.values(importantChangesConfirmed).every((value) => value)) {
                saveSettings(licenseKeyOverride);
            }
        },
        [
            saveSettings,
            importantChangesConfirmed,
            dispatch,
            modal,
            notifySuccess,
            i18n,
            Currentsettings.dataProtectionText,
            settings.dataProtectionText,
            Currentsettings.dsgvoVersionTimestamp,
        ],
    );

    useEffect(() => {
        if (pendingAction) {
            handleUpdateSettings();
            setPendingAction('');
        }
    }, [pendingAction, handleUpdateSettings]);

    useEffect(() => {
        setImportantChangesConfirmed((prev) => ({
            ...prev,
            icapAddress: Currentsettings.icapAddress === settings.icapAddress || settings.icapAddress === '',
        }));
    }, [Currentsettings.icapAddress, settings.icapAddress]);

    useEffect(() => {
        setImportantChangesConfirmed((prev) => ({
            ...prev,
            sensitivityLabels:
                JSON.stringify(Currentsettings.sensitivityLabels) === JSON.stringify(settings.sensitivityLabels),
        }));
    }, [Currentsettings.sensitivityLabels, settings.sensitivityLabels]);

    useEffect(() => {
        setImportantChangesConfirmed((prev) => ({
            ...prev,
        }));
    }, [Currentsettings.dataProtectionText, settings.dataProtectionText]);

    const SettingsActions = useMemo(
        () => ({
            handleUpdateSettings,
            removeDlpRegex,
            saveSettings,
            updateCurrentSettings,
            handleDlpRegexChange,
            handleSensitivityLabelChange,
            removeSensitivityLabel,
            saveReleaseChannel,
            handleAllowListChange,
            removeAllowListItem,
            handleAdditionalProtocolChange,
            removeAdditionalProtocol,
            setCurrentSettings,
        }),
        [
            handleUpdateSettings,
            removeDlpRegex,
            saveSettings,
            updateCurrentSettings,
            handleDlpRegexChange,
            handleSensitivityLabelChange,
            removeSensitivityLabel,
            saveReleaseChannel,
            handleAllowListChange,
            removeAllowListItem,
            handleAdditionalProtocolChange,
            removeAdditionalProtocol,
            setCurrentSettings,
        ],
    );

    const handleNavigate = useCallback(
        (path: string) => {
            navigate(path);
        },
        [navigate],
    );

    return {
        SettingsActions,
        setCurrentTab,
        getBreakpoint,
        dispatch,
        handleNavigate,
        models,
        embeddingModels,
        voiceModels,
        settingsLoaded,
        currentTab,
        userSettings,
        i18n,
        windowWidth,
        Currentsettings,
        mdParser,
    };
};

export default useSettings;
